import {DefaultPersonaImageKey} from "../assets/PersonaImage";
import {array_replace_by_index} from "../utils/functions";

export class CjmapPersona {

    private readonly is_show: boolean;
    private readonly tagline: string;
    private readonly image_key: string;
    private readonly specs: CjmapPersonaSpecs;
    private readonly description: string;

    constructor(is_show: boolean, tagline: string, image_key: string, specs: CjmapPersonaSpecs, description: string) {
        this.is_show = is_show;
        this.tagline = tagline;
        this.image_key = image_key;
        this.specs = specs;
        this.description = description;
    }

    private clone(replacement: {
        is_show?: boolean;
        tagline?: string;
        image_key?: string;
        specs?: CjmapPersonaSpecs;
        description?: string;
    }) {
        return new CjmapPersona(
            replacement.is_show === undefined ? this.is_show: replacement.is_show,
            replacement.tagline === undefined ? this.tagline : replacement.tagline,
            replacement.image_key === undefined ? this.image_key : replacement.image_key,
            replacement.specs === undefined ? this.specs : replacement.specs,
            replacement.description === undefined ? this.description : replacement.description,
        );
    }

    get IsShow(): boolean {
        return this.is_show;
    }

    withIsShow(is_show: boolean) {
        return this.clone({is_show});
    }

    get Tagline(): string {
        return this.tagline;
    }

    withTagline(tagline: string) {
        return this.clone({tagline});
    }

    get ImageKey(): string {
        return this.image_key;
    }

    withImageKey(image_key: string) {
        return this.clone({image_key});
    }

    get Specs(): CjmapPersonaSpecs {
        return this.specs;
    }

    withSpecs(specs: CjmapPersonaSpecs) {
        return this.clone({specs});
    }

    get Description(): string {
        return this.description;
    }

    withDescription(description: string) {
        return this.clone({description});
    }

    toJSON(): object {
        return {
            tagline: this.tagline,
            image_key: this.image_key,
            specs: this.specs.toJSON(),
            description: this.description
        }
    }

    static fromJSON(json: any): CjmapPersona {
        if (typeof (json.is_show) !== 'boolean' && typeof (json.is_show) !== 'undefined') {
            throw new Error('invalid is_show');
        }
        if (typeof (json.tagline) !== 'string') {
            throw new Error('invalid tagline');
        }
        if (typeof (json.image_key) !== 'string') {
            throw new Error('invalid image_key');
        }
        if (typeof (json.specs) !== 'object') {
            throw new Error('invalid specs');
        }
        if (typeof (json.description) !== 'string') {
            throw new Error('invalid description');
        }

        const is_show_or_undefined = json.is_show;
        const is_show = typeof (is_show_or_undefined) === 'boolean' ? is_show_or_undefined : true;

        return new CjmapPersona(is_show, json.tagline, json.image_key, CjmapPersonaSpecs.fromJSON(json.specs), json.description);
    }


    static createDefaultPersona(): CjmapPersona {
        return CjmapPersonaDefault.build();
    }

}

export class CjmapPersonaDefault {
    static DEFAULT_TAGLINE = "平日は仕事に打ち込み、休日は自分の時間を大切にしたい";
    static DEFAULT_IMAGE_KEY = DefaultPersonaImageKey;
    static DEFAULT_SPECS = [
        {name: "名前", value: "佐藤 愛さん"},
        {name: "性別", value: "女性"},
        {name: "年齢", value: "28歳"},
        {name: "職業", value: "IT企業の営業"},
        {name: "年収", value: "440万"},
        {name: "家族構成", value: "未婚、一人暮らし"},
        {name: "居住区域", value: "東京都世田谷区"}
    ];
    static DEFAULT_DESCRIPTION = ""
        + "休日は映画鑑賞と料理をすることが多い。最近はアロマオイルにはまっている。\n"
        + "大学進学時に上京し、地元に戻るつもりはあまりない。宮城県出身で、実家には60代の父母と大学生の妹が同居している。年1回は実家に帰るようにしている。\n"
        + "新卒から同じ会社にいるため、そろそろ転職も考えている。\n"
        + "大学時代の友人と年に2回ほど国内旅行に行くことが楽しみ。\n";

    static build(): CjmapPersona {
        return CjmapPersona.fromJSON({
            tagline: CjmapPersonaDefault.DEFAULT_TAGLINE,
            image_key: CjmapPersonaDefault.DEFAULT_IMAGE_KEY,
            specs: CjmapPersonaDefault.DEFAULT_SPECS,
            description: CjmapPersonaDefault.DEFAULT_DESCRIPTION
        })
    }
}

type SpecNameValue = {
    name: string;
    value: string
}

type SpecNameValueWithIndex = SpecNameValue & {
    index: number;
}

class CjmapPersonaSpecs {

    static SPEC_NUMBER = 7;

    private readonly specs: SpecNameValue[];

    constructor(specs: SpecNameValue[]) {
        if (specs.length !== CjmapPersonaSpecs.SPEC_NUMBER) {
            throw new Error('');
        }

        this.specs = specs;
    }

    getNameByIndex(index: number) {
        if (index < 0 || this.specs.length <= index) {
            throw new Error("out of range");
        }
        return this.specs[index].name;
    }

    withSpecName(index: number, name: string) {
        if (index < 0 || this.specs.length <= index) {
            throw new Error("out of range");
        }

        const value = this.specs[index].value;
        return new CjmapPersonaSpecs(
            array_replace_by_index(this.specs, index, {name, value})
        );
    }

    getValueByIndex(index: number) {
        if (index < 0 || this.specs.length <= index) {
            throw new Error("out of range");
        }
        return this.specs[index].value;
    }

    withSpecValue(index: number, value: string) {
        if (index < 0 || this.specs.length <= index) {
            throw new Error("out of range");
        }

        const name = this.specs[index].name;
        return new CjmapPersonaSpecs(
            array_replace_by_index(this.specs, index, {name, value})
        );
    }

    toArray(): SpecNameValueWithIndex[] {
        return this.specs.map(
            ({name, value}, index) => ({index, name, value})
        );
    }

    toJSON(): object {
        return this.specs.map(nv => Object.assign(nv));
    }

    static fromJSON(json: any): CjmapPersonaSpecs {

        const specs: SpecNameValue[] = [];
        for (const nv of json) {
            if (typeof (nv.name) !== 'string') {
                throw new Error('invalid name of specs');
            }
            if (typeof (nv.value) !== 'string') {
                throw new Error('invalid value of specs');
            }

            specs.push({name: nv.name, value: nv.value});
        }

        return new CjmapPersonaSpecs(specs);
    }

}
